import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getAnalytics, logEvent } from '@react-native-firebase/analytics';
import AuthNav from './auth/AuthStack';
import AppStack from './AppStack';
import OwnerSetupSessionBootstrap from '../screens/onboarding/OwnerSetupSessionBootstrap';
import { decideOwnerSetupEntry } from './ownerSetupEntryDecision';
import { resetOwnerActivationForIdentity } from '../redux/ownerActivationSlice';
import {
  clearOwnerSetupHandoffMarker,
  readOwnerSetupHandoffMarker,
} from '../services/ownerSetupSessionHandoff';
import {
  NavigationContainer,
  NavigationContainerRef,
} from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { AuthContext } from '../Context/AuthContext';
import { Modal, Platform, StatusBar, StyleSheet, View } from 'react-native';
// import Navloader from "../components/Reusables/NavLoader/Navloader";
import { useAppSelector } from '../redux/helper';
import { useDispatch } from 'react-redux';
import {
  cancelLoginPromptNotification,
  clearLegacyLoginPromptTriggers,
  scheduleLoginPromptNotification,
} from '../utils/Background';
import { useGettingStartedNotificationSchedule } from '../hooks/useGettingStartedNotificationSchedule';
import useBackofficeOwnerAuth from '../hooks/useBackofficeOwnerAuth';
import {
  flushGettingStartedTaskNavigation,
  setGettingStartedBackOfficePreparation,
  setGettingStartedRootNavigationRef,
} from './gettingStartedPushNav';
import {
  flushPendingDeepLink,
  setDeepLinkRootNavigationRef,
} from './deepLinks/deepLinkRouter';
import { useDeepLinkListener } from './deepLinks/useDeepLinkListener';
import { RepeatFrequency, Trigger, TriggerType } from '@notifee/react-native';
import SafeAreaWrapper from '../components/Reusables/SafeAreaWrapper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { SystemBars } from 'react-native-edge-to-edge';
import {
  setCurrentRouteName,
  setSecurityGateStatus,
  selectSecurityGateStatus,
} from '../redux/securityRuntimeSlice';
import SecurityRuntimeController from '../components/security/SecurityRuntimeController';
import { selectEmployeeRequireBiometricsOnForeground } from '../redux/selectors';
import SecurityGateContent from '../components/security/SecurityGateContent';
import { useTheme } from '../theme';

const AuthenticatedApp = () => {
  useEffect(() => {
    void flushPendingDeepLink();
  }, []);

  return (
    <SafeAreaWrapper statusBarStyle="light-content">
      <AppStack />
    </SafeAreaWrapper>
  );
};

const AppNav: React.FC = () => {
  const { theme } = useTheme();
  const RootStack = createNativeStackNavigator();
  const dispatch = useDispatch();
  const employeeInfo = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );
  const { auth } = useContext(AuthContext);
  const requiresBiometrics = useAppSelector(
    selectEmployeeRequireBiometricsOnForeground,
  );
  const securityGateStatus = useAppSelector(selectSecurityGateStatus);
  const preventsBiomtericsPrompt = useAppSelector(
    state => state.ui.preventsBiomtericsPrompt,
  );
  const routeNameRef = React.useRef<string | null>(null);
  const navigationRef = React.useRef<NavigationContainerRef<any> | null>(null);
  const loginPromptScheduledRef = React.useRef(false);
  const [legacyTriggersCleared, setLegacyTriggersCleared] = useState(
    Platform.OS === 'web',
  );

  // --- New-owner setup entry decision (US1) -------------------------------
  const ownerActivation = useAppSelector(state => state.ownerActivation);
  const isAuthenticated = !!employeeInfo?._id && !!auth?.accessToken?.length;
  const { autoLoginOwner } = useBackofficeOwnerAuth();
  useGettingStartedNotificationSchedule({
    isLoggedIn: isAuthenticated,
    legacyTriggersCleared,
  });
  useDeepLinkListener(isAuthenticated);

  useEffect(() => {
    setGettingStartedBackOfficePreparation(autoLoginOwner);
    return () => setGettingStartedBackOfficePreparation(null);
  }, [autoLoginOwner]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void flushGettingStartedTaskNavigation();
    void flushPendingDeepLink();
  }, [isAuthenticated]);

  const authenticatedOwnerKey = employeeInfo?.bp_owner_id
    ? String(employeeInfo.bp_owner_id)
    : null;
  // Transient handoff marker probe: detects a signup interrupted before the
  // eligibility flush (marker exists but Redux has no pending envelope).
  const [handoffMarkerProbe, setHandoffMarkerProbe] = useState<
    'checking' | 'present' | 'absent'
  >('checking');

  useEffect(() => {
    let cancelled = false;
    readOwnerSetupHandoffMarker()
      .then(marker => {
        if (!cancelled) setHandoffMarkerProbe(marker ? 'present' : 'absent');
      })
      .catch(() => {
        if (!cancelled) setHandoffMarkerProbe('absent');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // PersistGate gates this tree, so rehydration is always complete here.
  const ownerSetupEntry = decideOwnerSetupEntry({
    rehydrated: true,
    isAuthenticated,
    authenticatedOwnerKey,
    ownerActivation,
  });

  const requiresIdentityReset =
    ownerSetupEntry.kind === 'app' && ownerSetupEntry.requiresIdentityReset;

  useEffect(() => {
    // A different account signed in over another owner's persisted activation
    // progress: reset the residue before it can leak into the new session.
    if (requiresIdentityReset) {
      dispatch(
        resetOwnerActivationForIdentity({ ownerKey: authenticatedOwnerKey }),
      );
    }
  }, [authenticatedOwnerKey, dispatch, requiresIdentityReset]);

  const showSessionBootstrap =
    ownerSetupEntry.kind === 'session_recovery' ||
    (!isAuthenticated && handoffMarkerProbe === 'present');
  // Hold routing for the marker probe only when it can change the outcome
  // (logged-out cold start); never blocks authenticated users.
  const waitingForMarkerProbe =
    !isAuthenticated &&
    handoffMarkerProbe === 'checking' &&
    ownerSetupEntry.kind !== 'session_recovery';

  const handleBootstrapFinished = useCallback(() => {
    // ready/reset_and_continue flip Redux auth state on their own; for
    // requires_login/not_needed this releases routing to the Auth stack.
    setHandoffMarkerProbe('absent');
  }, []);

  useEffect(() => {
    // Marker outlived a session that already became ready (process died
    // between the ready flush and marker deletion): drop it silently.
    if (
      handoffMarkerProbe === 'present' &&
      isAuthenticated &&
      ownerSetupEntry.kind !== 'session_recovery'
    ) {
      void clearOwnerSetupHandoffMarker();
      setHandoffMarkerProbe('absent');
    }
  }, [handoffMarkerProbe, isAuthenticated, ownerSetupEntry.kind]);

  const scheduleNotification = useCallback(async () => {
    const date = new Date();
    date.setHours(8);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);

    // If 8 AM today has already passed, schedule for 8 AM tomorrow
    if (date.getTime() <= Date.now()) {
      date.setDate(date.getDate() + 1);
    }

    const trigger: Trigger = {
      type: TriggerType.TIMESTAMP,
      timestamp: date.getTime(),
      repeatFrequency: RepeatFrequency.DAILY,
    };

    try {
      const id = await scheduleLoginPromptNotification(trigger);
      console.log('Scheduled local notification for unlogged user:', id);
    } catch (error) {
      console.log('Error scheduling local notification:', error);
    }
  }, []);

  const cancelExistingNotifications = useCallback(async () => {
    try {
      await cancelLoginPromptNotification();
      console.log('Cancelled local login prompt notification');
    } catch (error) {
      console.log('Error cancelling notifications:', error);
    }
  }, []);

  useEffect(() => {
    const isLoggedIn = !!employeeInfo?._id && !!auth?.accessToken?.length;
    if (!isLoggedIn) {
      dispatch(setSecurityGateStatus('checking'));
      return;
    }

    if (Platform.OS === 'web') {
      dispatch(setSecurityGateStatus('unlocked'));
      return;
    }

    if (preventsBiomtericsPrompt) {
      dispatch(setSecurityGateStatus('unlocked'));
      return;
    }

    if (!requiresBiometrics) {
      dispatch(setSecurityGateStatus('unlocked'));
      return;
    }

    // requiresBiometrics is true: only lock when gate is still 'checking' (initial load).
    // Don't overwrite 'unlocked' when user just enabled the setting in SecuritySettingsScreen.
    if (securityGateStatus === 'unlocked') return;
    dispatch(setSecurityGateStatus('locked'));
  }, [
    auth?.accessToken?.length,
    dispatch,
    employeeInfo?._id,
    preventsBiomtericsPrompt,
    requiresBiometrics,
    securityGateStatus,
  ]);

  useEffect(() => {
    // notifee + trigger notifications are native-only
    if (Platform.OS === 'web') {
      setLegacyTriggersCleared(true);
      return;
    }

    let cancelled = false;
    void (async () => {
      // Await the one-time cancelAll so login + Getting Started schedules
      // cannot race and get wiped on first launch after upgrade.
      await clearLegacyLoginPromptTriggers();
      if (cancelled) return;
      setLegacyTriggersCleared(true);

      const isLoggedIn = !!employeeInfo?._id && !!auth?.accessToken?.length;
      if (!isLoggedIn) {
        if (!loginPromptScheduledRef.current) {
          loginPromptScheduledRef.current = true;
          await scheduleNotification();
        }
        return;
      }
      // Firebase handles push notifications for logged-in users.
      loginPromptScheduledRef.current = false;
      await cancelExistingNotifications();
    })();

    return () => {
      cancelled = true;
    };
  }, [
    auth?.accessToken?.length,
    cancelExistingNotifications,
    employeeInfo?._id,
    scheduleNotification,
  ]);

  // if (!fontsLoaded && !fontError) return <Navloader />;

  return (
    <View style={styles.container}>
      <SecurityRuntimeController />
      <NavigationContainer
        ref={instance => {
          navigationRef.current = instance;
          setGettingStartedRootNavigationRef(instance);
          setDeepLinkRootNavigationRef(instance);
        }}
        onReady={() => {
          // @ts-ignore
          routeNameRef.current = navigationRef.current?.getCurrentRoute()?.name;
          dispatch(setCurrentRouteName(routeNameRef.current));
          if (isAuthenticated) {
            void flushGettingStartedTaskNavigation();
            void flushPendingDeepLink();
          }
        }}
        onStateChange={async () => {
          const previousRouteName = routeNameRef.current;
          const currentRouteName =
            // @ts-ignore
            navigationRef.current?.getCurrentRoute()?.name;
          if (previousRouteName !== currentRouteName) {
            const analyticsInstance = getAnalytics();
            logEvent(analyticsInstance, 'screen_view', {
              firebase_screen: currentRouteName,
              firebase_screen_class: currentRouteName,
            });
          }
          routeNameRef.current = currentRouteName ?? null;
          dispatch(setCurrentRouteName(routeNameRef.current));
        }}>
        <RootStack.Navigator screenOptions={{ headerShown: false }}>
          {waitingForMarkerProbe ? (
            // One-tick gate while the handoff marker probe resolves; prevents
            // a Login flash before interrupted-signup recovery can render.
            <RootStack.Screen name="OwnerSetupBoot">
              {() => <View style={styles.bootGate} />}
            </RootStack.Screen>
          ) : showSessionBootstrap ? (
            <RootStack.Screen name="OwnerSetupBootstrap">
              {() => (
                <SafeAreaWrapper>
                  <OwnerSetupSessionBootstrap
                    onFinished={handleBootstrapFinished}
                  />
                </SafeAreaWrapper>
              )}
            </RootStack.Screen>
          ) : !isAuthenticated ? (
            <RootStack.Screen name="Auth">
              {() => (
                <SafeAreaWrapper>
                  <AuthNav />
                </SafeAreaWrapper>
              )}
            </RootStack.Screen>
          ) : (
            <RootStack.Screen name="App" component={AuthenticatedApp} />
          )}
        </RootStack.Navigator>
      </NavigationContainer>

      {/* Fullscreen security gate modal - overlays App when locked, preserves nav state */}
      {Platform.OS !== 'web' && (
        <Modal
          visible={
            !!(
              employeeInfo?._id &&
              auth?.accessToken?.length &&
              securityGateStatus === 'locked'
            )
          }
          transparent={false}
          animationType="fade"
          statusBarTranslucent={true}
          presentationStyle="fullScreen"
          onRequestClose={() => {}}>
          <SafeAreaView
            edges={['top', 'left', 'right', 'bottom']}
            style={[
              styles.securityGateModal,
              { backgroundColor: theme.colors.primaryDark },
            ]}>
            {Platform.OS === 'android' ? (
              <SystemBars style="light" />
            ) : (
              <StatusBar
                barStyle="light-content"
                backgroundColor={theme.colors.primaryDark}
                translucent={true}
                animated={true}
              />
            )}
            <SecurityGateContent />
          </SafeAreaView>
        </Modal>
      )}
    </View>
  );
};

export default AppNav;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  securityGateModal: {
    flex: 1,
  },
  bootGate: {
    flex: 1,
    backgroundColor: '#fff',
  },
});
